import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import AdminUploadedImages from "./AdminUploadedImages";
import {Image, Point} from "../../admin/artisticobjects/types/Image";
import {AdminButton, AdminFormSection, AdminInputDropdown, AdminInputText} from "./index";
import {FileUpload} from "primereact/fileupload";
import {classNames} from "primereact/utils";
import {useAdminConfirmDialog} from "../../common/hooks/useAdminConfirmDialog";
import {handleError} from "../../common/utils/errorHandling";
import imagesService, {ImageUploadResponse} from "../../admin/artisticobjects/services/imagesService";
import {Dialog} from "primereact/dialog";
import {IMAGE_TYPE_OPTIONS} from "../../admin/artisticobjects/types/ImageType";
import FocalPointSelector from "./FocalPointSelector";
import {getFeatureFlag} from "../../common/config/featureFlags";

type DialogTab = 'information' | 'focalPoint';

interface AdminUploadImagesProps {
  images: Image[];
  fileUploadRef: React.RefObject<FileUpload>;
  objectId: string;
  fetchObject: (objectId: string) => Promise<void>;
  uploadImage: (objectId: string, file: File) => Promise<ImageUploadResponse>;
  deleteImage: (objectId: string, imageId: string) => Promise<void>;
  reorderImages?: (objectId: string, imageIds: string[]) => Promise<void>;
  title?: string;
  chooseLabel?: string;
}

const AdminImagesSection: React.FC<AdminUploadImagesProps> = ({
                                                                images,
                                                                fileUploadRef,
                                                                objectId,
                                                                fetchObject,
                                                                uploadImage,
                                                                deleteImage,
                                                                reorderImages,
                                                                title,
                                                                chooseLabel
                                                              }) => {
  const {t} = useTranslation();
  const {showConfirmDialog} = useAdminConfirmDialog();
  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState<Image | undefined>();
  const [displayedImages, setDisplayedImages] = useState(images);
  const [activeTab, setActiveTab] = useState<DialogTab>('information');
  const [formData, setFormData] = useState({
    copyrights: '',
    imageType: ''
  });

  // Use translation as default if no prop provided
  const sectionTitle = title || t('admin.common.images.title');
  const buttonLabel = chooseLabel || t('admin.common.images.chooseImage');

  // Sync displayedImages with images prop when it changes
  useEffect(() => {
    setDisplayedImages(images);
  }, [images]);

  // Keep selectedImage in sync when the images prop refreshes (e.g. after reset)
  useEffect(() => {
    if (selectedImage) {
      const refreshed = images.find(img => img.id === selectedImage.id);
      if (refreshed) {
        setSelectedImage(refreshed);
      }
    }
  }, [images]);

  const handleImageUpload = async (event: any) => {
    const file = event.files[0];

    await handleError(
      async () => {
        await uploadImage(objectId, file);
        console.log(`Image ${file.name} uploaded successfully`);
        fileUploadRef.current?.clear()
        await fetchObject(objectId);
      },
      t('admin.common.images.errors.uploading'),
      () => {
        alert(`Error uploading image ${file.name}. Please try again.`);
      }
    );
  };

  const handleImageDelete = async (imageId: string, imageName: string, e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    showConfirmDialog({
      message: t('admin.common.messages.confirmDelete'),
      accept: async () => {
        await handleError(
          async () => {
            await deleteImage(objectId, imageId);
            console.log(`Image ${imageName} deleted successfully`);
            await fetchObject(objectId);
          },
          t('admin.common.images.errors.deleting'),
          () => {
            alert(`Error deleting image ${imageName}. Please try again.`);
          }
        );
      }
    });
  };

  const handleImageDetailsSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();

    if (!selectedImage) {
      return;
    }

    await handleError(
      async () => {
        await imagesService.updateImage(selectedImage.id, {
          copyrights: formData.copyrights || undefined,
          imageType: formData.imageType || undefined
        });
        console.log(`Image ${selectedImage.originalName} updated successfully`);
        setDialogVisible(false);
        await fetchObject(objectId);
      },
      t('admin.common.images.errors.updating'),
      () => {
        alert(`Error updating image ${selectedImage.originalName}. Please try again.`);
      }
    );
  };

  const handleFocalPointChange = async (point: Point) => {
    if (!selectedImage) return;

    await handleError(
      async () => {
        await imagesService.setFocalPoint(selectedImage.id, point);
        console.log(`Focal point for ${selectedImage.originalName} updated`);
        setSelectedImage(prev => prev ? {...prev, focalPoint: point} : prev);
        await fetchObject(objectId);
      },
      t('admin.common.images.errors.updatingFocalPoint', 'Error updating focal point'),
      () => {
        alert(`Error updating focal point for ${selectedImage.originalName}. Please try again.`);
      }
    );
  };

  const handleFocalPointReset = async () => {
    if (!selectedImage) return;

    await handleError(
      async () => {
        await imagesService.setFocalPoint(selectedImage.id, null);
        await fetchObject(objectId);
        // selectedImage will be synced via the useEffect that watches images
        console.log(`Focal point for ${selectedImage.originalName} reset`);
      },
      t('admin.common.images.errors.updatingFocalPoint', 'Error resetting focal point'),
      () => {
        alert(`Error resetting focal point for ${selectedImage.originalName}. Please try again.`);
      }
    );
  };

  const handleImageDetailsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {name, value} = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const {name, value} = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageEdit = (imageId: string, e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const image = images.find(img => img.id === imageId);
    setSelectedImage(image);
    setFormData({
      copyrights: image?.copyrights || '',
      imageType: image?.imageType || ''
    });
    setActiveTab('information');
    setDialogVisible(true);
  }

  const handleDialogHide = () => {
    if (!dialogVisible) return;
    setDialogVisible(false);
    setSelectedImage(undefined);
    setActiveTab('information');
    setFormData({
      copyrights: '',
      imageType: ''
    });
  }

  const handleImageOrderChange = async (sortedImages: Image[]) => {
    console.log('Image order changed', sortedImages);
    setDisplayedImages(sortedImages);

    if (reorderImages) {
      const imageIds = sortedImages.map(image => image.id);
      await handleError(
        async () => {
          await reorderImages(objectId, imageIds);
          console.log('Images reordered successfully');
        },
        t('admin.common.images.errors.reordering'),
        () => {
          // Revert to original order on error
          setDisplayedImages(images);
        }
      );
    }
  }

  const tabs = ([
    {key: 'information', label: t('admin.common.images.tabInformation', 'Information')},
    {key: 'focalPoint', label: t('admin.common.images.tabFocalPoint', 'Focal Point')},
  ] as {
    key: DialogTab;
    label: string
  }[]).filter(tab => tab.key !== 'focalPoint' || getFeatureFlag('focalPoints').manualOverrideEnabled);

  return (
    <AdminFormSection
      title={sectionTitle}
    >
      <FileUpload
        ref={fileUploadRef}
        mode="basic"
        name="images"
        accept="image/*"
        maxFileSize={40000000}
        customUpload
        auto
        uploadHandler={handleImageUpload}
        chooseLabel={buttonLabel}
        chooseOptions={{
          className: classNames('px-6 py-3 bg-gray-200 text-gray-700 rounded-sm hover:bg-gray-300 transition duration-200'),
          icon: 'hidden'
        }}
      />
      <Dialog
        header={t('admin.common.images.imageDetails')}
        visible={dialogVisible}
        style={{width: '60vw', maxWidth: '800px'}}
        onHide={handleDialogHide}
        maskClassName="bg-black bg-opacity-50"
      >
        {/* Lightweight tab bar */}
        {tabs.length > 1 && (
          <div className="flex border-b border-gray-200 mb-4">
            {tabs.map(tab => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={classNames(
                  'px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px',
                  activeTab === tab.key
                    ? 'border-gray-800 text-gray-800'
                    : 'border-transparent text-gray-400 hover:text-gray-600'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Tab: Information */}
        {activeTab === 'information' && (
          <form id="image-form" onSubmit={handleImageDetailsSubmit}>
            <div className="mt-2">
              <AdminInputDropdown
                label={t('admin.common.images.imageType')}
                value={formData.imageType}
                onChange={handleDropdownChange}
                options={IMAGE_TYPE_OPTIONS}
                placeholder={t('admin.common.images.selectImageType')}
                name="imageType"
                id="imageType"
                className="w-full"
              />
            </div>
            <div className="mt-4">
              <AdminInputText
                label={t('admin.common.images.imageCopyrights')}
                value={formData.copyrights}
                onChange={handleImageDetailsChange}
                name="copyrights"
                id="copyrights"
                className="w-full"
              />
            </div>
            <div className="flex justify-end gap-4 pt-6">
              <AdminButton
                label={t('admin.common.actions.cancel')}
                onClick={handleDialogHide}
                variant="white"
              />
              <AdminButton
                label={t('admin.common.actions.update')}
                onClick={handleImageDetailsSubmit}
                variant="black"
              />
            </div>
          </form>
        )}

        {/* Tab: Focal Point */}
        {activeTab === 'focalPoint' && selectedImage && (
          <FocalPointSelector
            image={selectedImage}
            focalPoint={selectedImage.focalPoint}
            onFocalPointChange={handleFocalPointChange}
            onReset={handleFocalPointReset}
          />
        )}
      </Dialog>

      <AdminUploadedImages
        images={displayedImages || []}
        onImageDelete={handleImageDelete}
        onImageEdit={handleImageEdit}
        onImageOrderChange={handleImageOrderChange}
      />
    </AdminFormSection>
  );
}

export default AdminImagesSection;